import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Section,
  SegmentedControl,
  Placeholder,
  Button,
  Spinner,
  Text,
} from '@telegram-apps/telegram-ui';
import { DealCard } from '../components/DealCard';
import { useDeals } from '../hooks/useDeals';
import { useAuth } from '../hooks/useAuth';
import { useCabinet } from '../hooks/useCabinet';
import { useTelegram } from '../hooks/useTelegram';
import { api } from '../api/client';
import type { DealsListFilter } from '../types';

type TabKey = 'all' | 'action';

const TABS = [
  { key: 'all' as TabKey, label: 'Заявки' },
  { key: 'action' as TabKey, label: 'Действия' },
];

/** Кнопки быстрых действий "Купить..." */
const BUY_BUTTONS = [
  { label: 'Купить 1С', icon: '1C', requestType: 'Покупка лицензии 1С' },
  { label: 'Купить Б24', icon: 'Б24', requestType: 'Покупка лицензии Битрикс24' },
  { label: 'Купить СТО-ХАБ', icon: 'СХ', requestType: 'Покупка СТО-ХАБ' },
  { label: 'Купить CRM PRO', icon: 'CRM', requestType: 'Покупка CRM PRO' },
];

export function DealsListPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { cabinet, isLoading: cabinetLoading } = useCabinet();
  const { hapticFeedback } = useTelegram();
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [buyingType, setBuyingType] = useState<string | null>(null);

  // Фильтр для API
  const filter: DealsListFilter | undefined = useMemo(() => {
    if (activeTab === 'action') return { actionRequired: true };
    return undefined;
  }, [activeTab]);

  const { deals, isLoading, error, refresh } = useDeals(filter);

  // Считаем бейдж "Требуют действия"
  const actionCount = deals.filter(d => d.isActionRequired).length;

  /** Обработчик "Купить..." — создаёт заявку менеджеру */
  async function handleBuyRequest(requestType: string) {
    const tg = (window as any).Telegram?.WebApp;

    const doRequest = async () => {
      setBuyingType(requestType);
      try {
        const result = await api.callback.request(undefined, requestType);
        hapticFeedback('success');
        if (tg?.showAlert) {
          tg.showAlert(result.message);
        }
      } catch (err) {
        hapticFeedback('error');
        const msg = err instanceof Error ? err.message : 'Ошибка отправки';
        if (tg?.showAlert) {
          tg.showAlert(msg);
        }
      } finally {
        setBuyingType(null);
      }
    };

    if (tg?.showConfirm) {
      tg.showConfirm(
        `Отправить заявку "${requestType}"? Менеджер свяжется с вами.`,
        (confirmed: boolean) => {
          if (confirmed) doRequest();
        }
      );
    } else {
      await doRequest();
    }
  }

  /** Звонок менеджеру */
  function handleCallManager(phone: string) {
    const tg = (window as any).Telegram?.WebApp;
    const telUrl = `tel:${phone.replace(/[\s()-]/g, '')}`;
    if (tg?.openLink) {
      tg.openLink(telUrl);
    } else {
      window.location.href = telUrl;
    }
  }

  return (
    <div style={{ paddingBottom: '80px' }}>
      {/* Брендированный заголовок */}
      <div style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '2px solid var(--stotek-red, #EC2226)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img
            src="/logo-stotek.svg"
            alt="СТОТЕК"
            style={{ height: '28px', objectFit: 'contain' }}
          />
        </div>
        <button
          onClick={async () => {
            const tg = (window as any).Telegram?.WebApp;
            if (tg?.showConfirm) {
              tg.showConfirm('Выйти из аккаунта?', async (confirmed: boolean) => {
                if (confirmed) {
                  await logout();
                  navigate('/auth', { replace: true });
                }
              });
            } else {
              await logout();
              navigate('/auth', { replace: true });
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '6px',
            fontSize: '13px',
            color: 'var(--tgui--hint_color, #999)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Выход
        </button>
      </div>

      {/* Блок компании и менеджера */}
      {cabinetLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
          <Spinner size="s" />
        </div>
      ) : cabinet ? (
        <div style={{ padding: '12px 16px' }}>
          {/* Название компании */}
          <Text weight="1" style={{ fontSize: '18px', marginBottom: '4px' }}>
            {cabinet.company.title}
          </Text>
          {cabinet.company.inn && (
            <Text style={{ fontSize: '13px', color: 'var(--tgui--hint_color, #999)', marginBottom: '12px' }}>
              ИНН: {cabinet.company.inn}
            </Text>
          )}

          {/* Менеджер */}
          {cabinet.manager && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              borderRadius: '12px',
              backgroundColor: 'var(--tgui--secondary_bg_color, #f5f5f5)',
            }}>
              {/* Аватар менеджера */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'var(--stotek-red, #EC2226)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}>
                {cabinet.manager.photo ? (
                  <img
                    src={cabinet.manager.photo}
                    alt={cabinet.manager.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span style={{ color: '#fff', fontSize: '16px', fontWeight: 600 }}>
                    {cabinet.manager.name.charAt(0)}
                  </span>
                )}
              </div>

              {/* Инфо менеджера */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                  {cabinet.manager.name}
                </Text>
                <Text style={{ fontSize: '12px', color: 'var(--tgui--hint_color, #999)' }}>
                  Ваш менеджер
                </Text>
              </div>

              {/* Кнопки связи */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {cabinet.manager.phone && (
                  <button
                    onClick={() => handleCallManager(cabinet.manager!.phone)}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: '#4CAF50',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07
                        19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1
                        4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0
                        1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1
                        2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => navigate('/callback')}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'var(--tgui--link_color, #2196F3)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Кнопки быстрых действий */}
      <div style={{ padding: '0 16px 12px' }}>
        <Text weight="2" style={{ fontSize: '14px', color: 'var(--tgui--hint_color, #999)', marginBottom: '8px' }}>
          Быстрые действия
        </Text>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px',
          marginBottom: '8px',
        }}>
          {/* Кнопки "Купить..." */}
          {BUY_BUTTONS.map((btn) => (
            <button
              key={btn.requestType}
              onClick={() => handleBuyRequest(btn.requestType)}
              disabled={buyingType === btn.requestType}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                padding: '10px 4px',
                borderRadius: '12px',
                border: '1px solid var(--tgui--divider_color, #e0e0e0)',
                backgroundColor: 'var(--tgui--bg_color, #fff)',
                cursor: buyingType === btn.requestType ? 'wait' : 'pointer',
                opacity: buyingType === btn.requestType ? 0.6 : 1,
                WebkitTapHighlightColor: 'transparent',
                color: 'inherit',
              }}
            >
              <span style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: 'rgba(236, 34, 38, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 700,
                color: 'var(--stotek-red, #EC2226)',
              }}>
                {btn.icon}
              </span>
              <span style={{ fontSize: '10px', fontWeight: 500, textAlign: 'center', lineHeight: '1.2' }}>
                {btn.label}
              </span>
            </button>
          ))}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '8px',
        }}>
          {/* Поставить задачу */}
          <button
            onClick={() => navigate('/create')}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '10px 4px',
              borderRadius: '12px',
              border: '1px solid var(--tgui--divider_color, #e0e0e0)',
              backgroundColor: 'var(--tgui--bg_color, #fff)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              color: 'inherit',
            }}
          >
            <span style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
            </span>
            <span style={{ fontSize: '10px', fontWeight: 500, textAlign: 'center', lineHeight: '1.2' }}>
              Поставить задачу
            </span>
          </button>

          {/* Техподдержка */}
          <button
            onClick={() => navigate('/create?category=0')}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '10px 4px',
              borderRadius: '12px',
              border: '1px solid var(--tgui--divider_color, #e0e0e0)',
              backgroundColor: 'var(--tgui--bg_color, #fff)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              color: 'inherit',
            }}
          >
            <span style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="#FF9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
            </span>
            <span style={{ fontSize: '10px', fontWeight: 500, textAlign: 'center', lineHeight: '1.2' }}>
              Техподдержка
            </span>
          </button>

          {/* Звонок менеджеру */}
          <button
            onClick={() => navigate('/callback')}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              padding: '10px 4px',
              borderRadius: '12px',
              border: '1px solid var(--tgui--divider_color, #e0e0e0)',
              backgroundColor: 'var(--tgui--bg_color, #fff)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              color: 'inherit',
            }}
          >
            <span style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07
                  19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1
                  4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0
                  1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1
                  2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </span>
            <span style={{ fontSize: '10px', fontWeight: 500, textAlign: 'center', lineHeight: '1.2' }}>
              Звонок менеджеру
            </span>
          </button>
        </div>
      </div>

      {/* Разделитель */}
      <div style={{ height: '1px', backgroundColor: 'var(--tgui--divider_color, #e0e0e0)', margin: '0 16px' }} />

      {/* Заголовок "Мои заявки" + табы */}
      <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text weight="1" style={{ fontSize: '18px' }}>
          Мои заявки
        </Text>
        <Button
          size="s"
          onClick={() => navigate('/create')}
        >
          Создать
        </Button>
      </div>

      {/* Табы */}
      <div style={{ padding: '0 16px 12px' }}>
        <SegmentedControl>
          {TABS.map((tab) => (
            <SegmentedControl.Item
              key={tab.key}
              selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {tab.key === 'action' && actionCount > 0 && (
                <span style={{
                  marginLeft: '4px',
                  background: '#ff3b30',
                  color: '#fff',
                  borderRadius: '8px',
                  padding: '1px 6px',
                  fontSize: '11px',
                  fontWeight: 600,
                }}>
                  {actionCount}
                </span>
              )}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl>
      </div>

      {/* Контент */}
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="m" />
        </div>
      )}

      {error && (
        <Placeholder
          header="Ошибка загрузки"
          description={error}
          action={
            <Button size="m" onClick={refresh}>
              Повторить
            </Button>
          }
        />
      )}

      {!isLoading && !error && deals.length === 0 && (
        <Placeholder
          header="Нет заявок"
          description={
            activeTab === 'action'
              ? 'Нет заявок, требующих вашего действия'
              : 'У вас пока нет активных заявок'
          }
          action={
            <Button size="m" onClick={() => navigate('/create')}>
              Создать заявку
            </Button>
          }
        />
      )}

      {!isLoading && !error && deals.length > 0 && (
        <Section>
          {deals.map((deal) => (
            <DealCard key={deal.id} deal={deal} />
          ))}
        </Section>
      )}

      {/* FAB */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 100,
        }}
      >
        <Button
          size="l"
          onClick={() => navigate('/create')}
          style={{
            borderRadius: '50%',
            width: '56px',
            height: '56px',
            minWidth: '56px',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          +
        </Button>
      </div>
    </div>
  );
}
